<template>
  <li class="OptionList__Item" :data-value="value" v-show="isVisible(value)">
    <div class="OptionList__Item-Inner" :class="[handleActive, handleAvailability]">
      <button v-if="option === 'Color'" aria-label="Color Swatch" class="OptionList__CTA" @click="emits('handle-selection', value)">
        <div role="presentation" class="OptionList__Swatch" v-if="isHex(value)" :style="fetchSwatch(value)" data-ht-element="swatch" :data-ht-id="value"></div>
        <img v-else class="OptionList__Swatch OptionList__Swatch--Img" :src="fetchSwatch(value)" data-ht-element="swatch" :data-ht-id="value" :alt="value" />
      </button>
      <button v-else :data-ht-element="option" :data-ht-id="value" class="button button--tertiary-a" @click="emits('handle-selection', value)">
        {{ formattedValue }}
      </button>
    </div>
    <p class="OptionList__Item-Label" :style="labelStyle" v-if="hasLabel">
      {{ labelMessage }}
    </p>
  </li>
</template>

<script setup>
import { arraysEqual } from '@utils/performance';
import { useProductStore } from './Composables/Store';
import { formatValue } from '@utils/ProductResources';
import { fetchSwatch, isHex } from '../Global/Composables/ProductCard';
import { computed, ref } from 'vue';

const available = ref(true);
const active = ref(false);
const store = useProductStore();
const emits = defineEmits(['handle-selection']);
const props = defineProps({
  data: { type: Object, required: false, default: false },
  option: { type: String, required: true },
  value: { type: String, required: true },
  index: { type: Number, required: true },
  active: { type: String, default: '' },
  isAutoBundle: { type: Boolean, required: false, default: false },
  isGiftCard: { type: Boolean, required: false, default: false },
  location: { type: String, required: false },
});

const formattedValue = computed(() => props?.value?.replace('Alternative', 'Alt'));

const handleAvailability = computed(() => {
  if (props.isAutoBundle) {
    return available.value ? '' : 'OptionList__Item-Inner--Unavailable';
  }

  let variants = props.data ? props.data.selected.matches : store.$state.selected.matches;

  const options = props.data ? props.data.selected.options : store.$state.selected.options;

  const totalOptions = props.data ? props.data.selected.options.length : store.$state.totalOptions;

  if (totalOptions < 2) {
    variants = props.data ? props.data.item.variants : store.$state.item.variants;
  }

  const selections = options.filter((option) => option !== '');

  const enoughSelectionsMade = selections.length >= totalOptions;

  /**
   * @var {Undefined} partialMatch is initially undefined.
   * partialMatch will eventually be reassigned to the returned value of our variant lookup
   */
  let partialMatch;

  /**
   * @var {Boolean} indexSelected is the returned value if the current index of the option list has been selected.
   * This is really only relevant for when PDPs contain the 'galopt=true' URL param where not all options have been selected.
   * @example indexSelected = true
   * @example indexSelected = false
   */
  const indexSelected = options[props.index] !== '';

  if (indexSelected && !enoughSelectionsMade) {
    /**
     * @var {Object, Undefined} partialMatch will attempt to find the first available variant to default to
     * based on the selection made and the if there's inventory.
     * If there aren't variants with inventory, partialMatch will return undefined.
     */
    partialMatch = variants.find((variant) => {
      const { selectedOptions, quantityAvailable } = variant;
      const matchingOptionValue = selectedOptions[props.index].value === props.value;
      return matchingOptionValue && quantityAvailable;
    });
    if (!partialMatch) {
      /**
       * @var {Object, Undefined} partialMatch if the previous attempt to reassign partialMatch returns undefined
       * Meaning there are no available variants based on a particular selection, we will default to the first variant
       * to appropriately set the available state to false.
       */
      partialMatch = variants.find((variant) => variant?.selectedOptions[props.index].value === props.value);
    }
  } else {
    partialMatch = variants.find((variant) => variant?.selectedOptions[props.index].value === props.value);
  }

  if (partialMatch === undefined) {
    available.value = available.value;
  } else if (partialMatch.quantityAvailable <= 0 && !props.isGiftCard) {
    available.value = false;
  } else {
    available.value = true;
  }

  if (enoughSelectionsMade) {
    //find the variant that satisfies the selected options
    //if match does not exist given the selected options, default to first partial match
    let match = variants.find((variant) => arraysEqual(variant.selectedOptions, selections)) || props?.data?.selected?.matches[0];

    const selectedValues = match.selectedOptions?.map((option) => option.value);
    const foundInMatch = selectedValues.includes(props.value);

    if (match.quantityAvailable <= 0 && foundInMatch && !props.isGiftCard) {
      available.value = false;
    } else if (foundInMatch) {
      available.value = true;
    }
  }
  return available.value ? '' : 'OptionList__Item-Inner--Unavailable';
});

const isVisible = (value) => {
  const hiddenOptions = store?.$state?.hiddenOptions ?? [];
  const isVisible = hiddenOptions.filter((option) => option === value).length === 0;
  return isVisible;
};

const handleActive = computed(() => {
  active.value = props.active === formatValue(props.value);
  return active.value ? 'OptionList__Item-Inner--Active' : '';
});

const hasLabel = computed(() => labelIndex.value > -1);

const labelIndex = computed(() => {
  const item = props.data?.item ?? false;
  if (!item) return -1;

  const promotedOptions = item?.promotedOptionGroups ?? [];

  if (promotedOptions.length <= 0) return -1;

  const index = promotedOptions.findIndex((group) => {
    if (group.values.value && typeof group.values.value == 'string') {
      const { value } = group.values;
      const replaceGroup = value.replace(/\[|\]|\"/g, '');
      const newArray = replaceGroup.split(',');
      group.values = newArray;
    }
    return group.values.includes(props.value);
  });
  return index;
});
const labelMessage = computed(() => {
  const item = props.data?.item;
  const promotedOptions = item?.promotedOptionGroups ?? [];

  const group = promotedOptions[labelIndex.value] ?? false;

  return group?.label?.value ?? '';
});
const labelStyle = computed(() => {
  const item = props.data?.item;
  const promotedOptions = item?.promotedOptionGroups ?? [];
  const group = promotedOptions[labelIndex.value];

  const color = group?.color ?? { value: '' };
  if (color.value && color.value !== '') {
    return `color: ${group.color.value}`;
  }
  return '';
});
</script>
